import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ConfirmationModal, SectionTitle } from 'modules/shared/components';
import useAsyncData from 'modules/shared/hooks/useAsyncData';
import { formatAmount, formatDate } from 'modules/shared/utils';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import {
  EmployeeExit,
  completeEmployeeExit,
  getEmployeeExit,
  saveEmployeeExit,
} from '../people.api';

const EXIT_TYPES = ['Resigned', 'Terminated', 'Retired', 'Contract Ended'];

const CLEARANCE: Array<{ key: keyof EmployeeExit; label: string }> = [
  { key: 'exit_interview_done', label: 'Exit interview done' },
  { key: 'assets_returned', label: 'Company assets returned' },
  { key: 'knowledge_transfer_done', label: 'Knowledge transfer done' },
  { key: 'dues_cleared', label: 'Dues cleared' },
];

const blank = {
  exit_type: 'Resigned',
  resignation_date: '',
  last_working_day: '',
  notice_days: '',
  reason: '',
  exit_interview_done: false,
  assets_returned: false,
  knowledge_transfer_done: false,
  dues_cleared: false,
  fnf_amount: '',
  fnf_paid_on: '',
  remarks: '',
};

const toForm = (exit: EmployeeExit | null) =>
  !exit
    ? { ...blank }
    : {
        exit_type: exit.exit_type ?? 'Resigned',
        resignation_date: exit.resignation_date ?? '',
        last_working_day: exit.last_working_day ?? '',
        notice_days: exit.notice_days != null ? String(exit.notice_days) : '',
        reason: exit.reason ?? '',
        exit_interview_done: Boolean(exit.exit_interview_done),
        assets_returned: Boolean(exit.assets_returned),
        knowledge_transfer_done: Boolean(exit.knowledge_transfer_done),
        dues_cleared: Boolean(exit.dues_cleared),
        fnf_amount: exit.fnf_amount != null ? String(exit.fnf_amount) : '',
        fnf_paid_on: exit.fnf_paid_on ?? '',
        remarks: exit.remarks ?? '',
      };

/**
 * Offboarding: notice, clearance and full-and-final.
 *
 * Leaving used to be three columns edited by hand — employment status, leaving date, leaving
 * reason — with nothing recording whether assets had come back or dues were settled. Completing
 * the exit here is what writes those three columns, and it will not close until the clearance
 * checklist is done.
 */
export default function EmployeeExitSection({
  employeeId,
  employeeName,
  onCompleted,
}: {
  employeeId: number | string;
  employeeName?: string;
  onCompleted?: () => void | Promise<void>;
}) {
  const fetcher = useCallback(() => getEmployeeExit(employeeId), [employeeId]);
  const { data, loading, error, refetch } = useAsyncData<EmployeeExit | null>(fetcher, null, {
    code: 'EMPLOYEE_EXIT_FAILED',
    errorMessage: 'Failed to load the exit record',
    toastOnError: false,
  });

  const [form, setForm] = useState(toForm(null));
  const [started, setStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setForm(toForm(data));
    setStarted(Boolean(data));
  }, [data]);

  const completed = Boolean(data?.completed_at);
  const clearanceDone = CLEARANCE.every(({ key }) => form[key as keyof typeof form]);

  const set = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setBusy(true);
    try {
      await saveEmployeeExit(employeeId, {
        ...form,
        notice_days: form.notice_days === '' ? null : Number(form.notice_days),
        fnf_amount: form.fnf_amount === '' ? null : Number(form.fnf_amount),
        resignation_date: form.resignation_date || null,
        last_working_day: form.last_working_day || null,
        fnf_paid_on: form.fnf_paid_on || null,
      } as Partial<EmployeeExit>);
      toast.success('Exit saved');
      await refetch();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to save the exit', code: 'EMPLOYEE_EXIT_SAVE_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async () => {
    setBusy(true);
    try {
      await completeEmployeeExit(employeeId);
      toast.success('Exit completed');
      setConfirming(false);
      await refetch();
      await onCompleted?.();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to complete the exit', code: 'EMPLOYEE_EXIT_COMPLETE_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap={2}>
      <Divider />
      <SectionTitle
        title='Exit & clearance'
        action={
          completed ? (
            <Chip size='small' color='success' label={`Completed ${formatDate(data!.completed_at!)}`} />
          ) : started ? (
            <Chip size='small' color='warning' label='In progress' />
          ) : null
        }
      />

      {error && (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!started && !loading && (
        <Stack direction='row' gap={2} alignItems='center'>
          <Typography variant='body2' color='text.secondary'>
            No exit has been started for this employee.
          </Typography>
          <Button variant='outlined' onClick={() => setStarted(true)}>
            Start exit
          </Button>
        </Stack>
      )}

      {started && (
        <Stack gap={2}>
          <Stack direction='row' gap={2} flexWrap='wrap'>
            <TextField
              select
              size='small'
              label='Exit type'
              value={form.exit_type}
              onChange={(e) => set('exit_type', e.target.value)}
              sx={{ minWidth: 180 }}
              disabled={completed}
            >
              {EXIT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size='small'
              type='date'
              label='Resignation date'
              value={form.resignation_date}
              onChange={(e) => set('resignation_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={completed}
            />
            <TextField
              size='small'
              type='date'
              label='Last working day'
              value={form.last_working_day}
              onChange={(e) => set('last_working_day', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={completed}
            />
            <TextField
              size='small'
              type='number'
              label='Notice days'
              value={form.notice_days}
              onChange={(e) => set('notice_days', e.target.value)}
              sx={{ width: 140 }}
              disabled={completed}
            />
          </Stack>

          <TextField
            size='small'
            label='Reason'
            value={form.reason}
            onChange={(e) => set('reason', e.target.value)}
            multiline
            minRows={2}
            disabled={completed}
            helperText='Written onto the employee record when the exit is completed.'
          />

          <Stack direction='row' gap={3} flexWrap='wrap'>
            {CLEARANCE.map(({ key, label }) => (
              <FormControlLabel
                key={String(key)}
                control={
                  <Checkbox
                    checked={Boolean(form[key as keyof typeof form])}
                    onChange={(e) => set(String(key), e.target.checked)}
                    disabled={completed}
                  />
                }
                label={label}
              />
            ))}
          </Stack>

          <Stack direction='row' gap={2} flexWrap='wrap'>
            <TextField
              size='small'
              type='number'
              label='Full & final amount (₹)'
              value={form.fnf_amount}
              onChange={(e) => set('fnf_amount', e.target.value)}
              sx={{ minWidth: 200 }}
              disabled={completed}
            />
            <TextField
              size='small'
              type='date'
              label='F&F paid on'
              value={form.fnf_paid_on}
              onChange={(e) => set('fnf_paid_on', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={completed}
            />
          </Stack>

          <TextField
            size='small'
            label='Remarks'
            value={form.remarks}
            onChange={(e) => set('remarks', e.target.value)}
            multiline
            minRows={2}
            disabled={completed}
          />

          {completed ? (
            <Alert severity='success'>
              {employeeName ?? 'This employee'} was marked {data?.exit_type} with a last working
              day of {formatDate(data!.last_working_day!)}
              {data?.fnf_amount ? `, full and final ${formatAmount(data.fnf_amount)}` : ''}. The
              record is now closed.
            </Alert>
          ) : (
            <>
              {!clearanceDone && (
                <Alert severity='info'>
                  All four clearance steps must be ticked before the exit can be closed.
                </Alert>
              )}
              <Stack direction='row' gap={2} justifyContent='flex-end'>
                <Button onClick={handleSave} disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant='contained'
                  color='error'
                  onClick={() => setConfirming(true)}
                  disabled={busy || !clearanceDone || !form.last_working_day}
                >
                  Complete exit
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}

      <ConfirmationModal
        open={confirming}
        onClose={() => setConfirming(false)}
        loading={busy}
        icon='mdi:account-arrow-right-outline'
        title='Complete this exit?'
        message={`${employeeName ?? 'The employee'} will be marked ${form.exit_type} with a leaving date of ${
          form.last_working_day ? formatDate(form.last_working_day) : '—'
        }. Their login stays, but they will no longer appear as active staff and will be left out of payroll months opened after this date.`}
        confirmText='Complete exit'
        onConfirm={handleComplete}
      />
    </Stack>
  );
}
